import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { EntityType } from '../../../redux/Global.model';
import { OrderCommodities } from '../../../redux/api/orders/orders.model';
import { TripCommodities } from '../../../redux/api/trips/trips.model';
import TwField, { TwInputTypes } from '../../TwFields/TwFields';
import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

interface TwMobileCommodityDetailsProps {
  entity: EntityType;
  commodities: OrderCommodities[] | TripCommodities[];
  onDataUpdate: (commodity: OrderCommodities | TripCommodities) => void;
  onRemove: (id: string) => void;
}

interface TwMobileCommodityItemProps {
  entity: EntityType;
  commodity: TripCommodities;
  onDataUpdate: (commodity: OrderCommodities | TripCommodities) => void;
  onRemove: (id: string) => void;
}

export const CommodityContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  backgroundColor: '#FFFFFF',
  padding: '16px',
  border: '1px solid #D1D5DB',
  borderRadius: '4px',
  marginBottom: '10px'
}));

export const RowLabel = styled('label')(() => ({
  fontFamily: 'Poppins',
  fontSize: '14px',
  color: '#6B7280',
  textTransform: 'capitalize',
  fontWeight: 400,
  lineHeight: '20px',
  width: '30%',
  display: 'flex',
  alignItems: 'center'
}));

export const FieldRowContainer = styled('div')(() => ({
  display: 'flex',
  gap: '16px',
  width: '100%',
  height: '40px'
}));

export const FieldInputContainer = styled('div')(() => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center'
}));

export const QtyWeightContainer = styled('div')(() => ({
  display: 'flex',
  gap: '10px',
  height: '100%'
}));

export const TwMobileCommodityDetails: React.FC<TwMobileCommodityDetailsProps> = ({
  entity,
  commodities,
  onRemove,
  onDataUpdate
}) => {
  return (
    <Box>
      {commodities.map((commodity) => {
        return (
          <TwMobileCommodityItem
            key={commodity.ui_uid}
            entity={entity}
            commodity={commodity}
            onDataUpdate={onDataUpdate}
            onRemove={onRemove}
          />
        );
      })}
    </Box>
  );
};

const TwMobileCommodityItem: React.FC<TwMobileCommodityItemProps> = ({
  entity,
  commodity,
  onDataUpdate,
  onRemove
}) => {
  const [description, setDescription] = useState<string>(commodity.description ?? '');
  const [quantity, setQuantity] = useState<number | null>(commodity.quantity);
  const [weight, setWeight] = useState<number | null>(commodity.weight);

  return (
    <CommodityContainer>
      <FieldRowContainer>
        <RowLabel>Description</RowLabel>
        <FieldInputContainer sx={{ justifyContent: 'space-between' }}>
          {commodity.order_id ? (
            <span>{commodity.description || '-'}</span>
          ) : (
            <TwField
              value={description || ''}
              name="commodity-description"
              onInputChange={(e): void => setDescription(e.target.value)}
              onInputBlur={(e): void => {
                onDataUpdate({ ...commodity, description });
              }}
            />
          )}
          <IconButton
            disableRipple
            aria-label="remove-commodity"
            onClick={(): void => onRemove(commodity.ui_uid)}
          >
            <CloseIcon />
          </IconButton>
        </FieldInputContainer>
      </FieldRowContainer>
      {entity === EntityType.TRIP && (
        <>
          <FieldRowContainer>
            <RowLabel>Customer</RowLabel>
            <FieldInputContainer>
              <span>{commodity.customer || '-'}</span>
            </FieldInputContainer>
          </FieldRowContainer>
          <FieldRowContainer>
            <RowLabel>Cust. #</RowLabel>
            <FieldInputContainer>
              <span>{commodity.customer_number || '-'}</span>
            </FieldInputContainer>
          </FieldRowContainer>
          <FieldRowContainer>
            <RowLabel>Order #</RowLabel>
            <FieldInputContainer>
              <span>{commodity.order_readable_id || '-'}</span>
            </FieldInputContainer>
          </FieldRowContainer>
        </>
      )}
      <FieldRowContainer>
        <RowLabel>Qty x Weight</RowLabel>
        <FieldInputContainer>
          <QtyWeightContainer>
            <Box sx={{ width: '120px' }}>
              <TwField
                inputType={TwInputTypes.number}
                value={quantity || ''}
                name="commodity-quantity"
                onInputChange={(e): void => setQuantity(parseInt(e.target.value))}
                onInputBlur={(e): void => {
                  onDataUpdate({ ...commodity, quantity });
                }}
              />
            </Box>
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>x</Typography>
            <TwField
              inputType={TwInputTypes.number}
              value={weight || ''}
              name="commodity-weight"
              onInputChange={(e): void => setWeight(parseInt(e.target.value))}
              endAdorment="lbs"
              onInputBlur={(e): void => {
                onDataUpdate({ ...commodity, weight: weight });
              }}
            />
          </QtyWeightContainer>
        </FieldInputContainer>
      </FieldRowContainer>
    </CommodityContainer>
  );
};
